import { Inline } from 'canoe';
import React, { useEffect, useRef, useState } from 'react';

import { ValueBox, ValueBoxGroupOptions, ValueStepper } from '@candu-ai/craft/ui';
import { SizeType, SizeValue } from 'craft/components/Size';

import { SizeTypeSelector } from './SizeTypeSelector';

interface SizeStepperProps {
  label: string;
  type: SizeType;
  value: SizeValue;
  onChange: (value: SizeValue) => void;
  onChangeType: (type: SizeType, forceValueChange?: boolean) => void;
  valueSuffix?: string;
  placeholder?: string;
  enabled?: boolean;
  shouldShowAuto?: boolean;
  shouldShowPercentage?: boolean;
  name: string;
  inGroupProps?: ValueBoxGroupOptions;
  property: 'min' | 'max' | 'normal' | 'gap';
  minValue?: number;
  maxValue?: number;
  onReset?: () => void;
}

export const SizeStepper = ({
  label,
  name,
  value,
  type,
  enabled,
  inGroupProps,
  placeholder = 'unset',
  onChange,
  onChangeType,
  shouldShowAuto = false,
  shouldShowPercentage,
  property,
  minValue = 0,
  maxValue = 10000,
  onReset,
  valueSuffix,
}: SizeStepperProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [inputValue, setInputValue] = useState<number | undefined>(value as number | undefined);
  const [inEditMode, setInEditMode] = useState(false);
  const [sizeType, setSizeType] = useState<SizeType>(type);

  const selectInput = () => {
    inputRef.current?.select();
  };

  const onSetValue = (newValue?: number) => {
    const valueChanged = newValue !== inputValue;
    setInEditMode(true);

    if (valueChanged) {
      setInputValue(newValue);

      // only trigger change when there is no value or the value is within the accepted range
      if (typeof newValue === 'undefined') {
        onChange(undefined);
      } else if (newValue >= minValue && newValue <= maxValue) {
        onChange(newValue);
      }
    }
  };

  const onSetType = (newType: SizeType) => {
    setInEditMode(false);
    onChangeType(newType);
    setSizeType(newType);
    selectInput();
  };

  useEffect(() => {
    // when value or type changes due to external factors (caller auto-adjusted value/type, viewport changed, node changed, etc.)
    // if we're not in edit mode, reflect value/type change in input
    if (!inEditMode) {
      if (value !== inputValue) {
        setInputValue(value as number | undefined);
      }
      if (type !== sizeType) {
        setSizeType(type);
      }
    }

    // when type changes while editing, make sure the correct type is selected & reflected
    if (inEditMode && typeof inputValue !== 'undefined' && type !== sizeType) {
      if (sizeType !== SizeType.Default && sizeType !== SizeType.Auto) {
        onChangeType(sizeType, false);
      } else {
        setSizeType(type);
      }
    }
  }, [value, type, inEditMode]);

  return (
    <ValueBox label={label} isInGroup={inGroupProps}>
      <>
        <ValueStepper
          ref={inputRef}
          enabled={enabled}
          step={1}
          name={name}
          placeholder={!inEditMode ? placeholder : undefined}
          valueSuffix={valueSuffix}
          editable
          format="inline"
          minValue={minValue}
          maxValue={maxValue}
          onChange={onSetValue}
          onBlur={() => {
            setInEditMode(false);
          }}
          value={inputValue}
          onReset={onReset}
        />
        <Inline space="none">
          <SizeTypeSelector
            isLast={name === 'height'}
            value={sizeType}
            onChange={onSetType}
            showAuto={shouldShowAuto}
            showPercentage={shouldShowPercentage}
            property={property}
          />
        </Inline>
      </>
    </ValueBox>
  );
};
